import { Component, Inject, LOCALE_ID, Renderer2, DOCUMENT, NgZone } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { filter, map } from 'rxjs/operators';
import { ConfigService } from '../@vex/services/config.service';
import { environment } from '../environments/environment';
import moment from 'moment';

import { Platform } from '@angular/cdk/platform';
import { NavigationService } from '../@vex/services/navigation.service';
import { LayoutService } from '../@vex/services/layout.service';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { SplashScreenService } from '../@vex/services/splash-screen.service';
import { Style, StyleService } from '../@vex/services/style.service';
import { ConfigName } from '../@vex/interfaces/config-name.model';
import { AppearanceService } from './helpers/services/appearance.service';
import { SelectLiveSearchService } from './helpers/services/select-live-search.service';



@Component({
    selector: 'vex-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent {
  title = 'vex';

  constructor(private configService: ConfigService,

    private styleService: StyleService,
    private renderer: Renderer2,
    private platform: Platform,
    @Inject(DOCUMENT) private document: Document,
    @Inject(LOCALE_ID) private localeId: string,
    private layoutService: LayoutService,
    private route: ActivatedRoute,
    private navigationService: NavigationService,
    private router: Router,
    private ngZone: NgZone,
    private splashScreenService: SplashScreenService,
    private selectLiveSearch: SelectLiveSearchService,
    private appearance: AppearanceService) {
    this.selectLiveSearch.start();
    moment.locale(this.localeId);

    if (this.platform.BLINK) {
      this.renderer.addClass(this.document.body, 'is-blink');
    }


    /**
     * Config Related Subscriptions
     * You can remove this if you don't need the functionality of being able to enable specific configs with queryParams
     * Example: example.com/?layout=apollo&style=default
     */
    this.route.queryParamMap.pipe(
      filter(queryParamMap => queryParamMap.has('rtl')),
      map(queryParamMap => coerceBooleanProperty(queryParamMap.get('rtl'))),
    ).subscribe(isRtl => {
      this.configService.updateConfig({
        rtl: isRtl
      });
    });

    this.route.queryParamMap.pipe(
      filter(queryParamMap => queryParamMap.has('layout'))
    ).subscribe(queryParamMap => this.configService.setConfig(queryParamMap.get('layout') as ConfigName));

    this.route.queryParamMap.pipe(
      filter(queryParamMap => queryParamMap.has('style'))
    ).subscribe(queryParamMap => this.styleService.setStyle(queryParamMap.get('style') as Style));


    /**
     * Add your own routes here
     */
    this.navigationService.items = [
      {
        type: 'link',
        label: 'My Sample Offers',
        role: ['vendor'],
        route: '/apps/sample-offers',
        icon: 'local_shipping'
      },
      {
        type: 'dropdown',
        label: 'RFQ',
        role: ['admin', 'sales', 'vendor', 'designer', 'production', 'sourcing_manager'],
        route: '/apps/rfq',
        icon: 'pi pi-home',
        children: [
          {
            type: 'link',
            label: 'Standardized',
            role: ['admin', 'sales', 'vendor', 'designer', 'production', 'sourcing_manager'],
            route: '/apps/standardized',
            icon: 'pi pi-building'
          },
          {
            type: 'link',
            label: 'Archive',
            route: '/apps/archive',
            icon: 'pi pi-folder-open'
          },
          {
            type: 'link',
            label: 'HTS lookup',
            role: ['admin', 'sales', 'production', 'sourcing_manager'],
            route: '/apps/rfq/lookup',
            icon: 'pi pi-search'
          }
        ]
      },
      {
        type: 'dropdown',
        label: 'Item library',
        role: ['admin', 'sales', 'designer', 'production','sourcing_manager'],
        route: '/apps/itemLibrary',
        icon: 'pi pi-book',
        children: [
          {
            type: 'dropdown',
            label: 'Art Piece',
            role: ['admin', 'sales', 'designer', 'production'],
            route: '/apps/art-piece',
            icon: 'pi pi-palette',
            children: [
              {
                type: 'link',
                label: 'Archive',
                route: '/apps/art-piece/archive',
                icon: 'pi pi-folder-open'
              }
            ]
          },
          {
            type: 'link',
            label: 'Archive',
            route: '/apps/itemLibrary/archive',
            icon: 'pi pi-folder-open'
          }
        ]
      },
      {
        type: 'link',
        label: 'Factory',
        role: ['vendor'],
        route: '/apps/factory',
        icon: 'pi pi-shop',
      },
      {
        type: 'dropdown',
        label: 'Tracking',
        role: ['admin', 'production', 'sourcing_manager', 'designer'],
        route: '',
        icon: 'pi pi-qrcode',
        children: [
          {
            type: 'link',
            label: 'Licensing',
            route: '/apps/licensing-tracking',
            icon: 'badge'
          },
          {
            type: 'link',
            label: 'Production',
            route: '/apps/prod-tracking',
            icon: 'production_quantity_limits'
          },
          {
            type: 'link',
            label: 'Unmatched Sales Orders',
            route: '/apps/unmatched-sales-orders',
            icon: 'link_off'
          },
          {
            type: 'link',
            label: 'Samples',
            route: '/apps/sample-tracking',
            icon: 'inventory_2'
          },
          {
            type: 'link',
            label: 'Lead Time Calculator',
            route: '/apps/lead-time-calculator',
            icon: 'calculate'
          }]
      },
      {
        type: 'dropdown',
        label: 'Admin',
        route: '',
        role: ['admin','sourcing_manager', 'production', 'designer', 'sales'],
        icon: 'ic:twotone-settings',
        children: [
          {
            type: 'link',
            label: 'Editor',
            route: '/apps/admin/editor',
            icon: 'edit'
          },
          {
            type: 'link',
            label: 'Manage users',
            role: ['admin'],
            route: '/apps/admin/users',
            icon: 'manage_accounts'

          },
          {
            type: 'link',
            label: 'Roles & Access',
            role: ['admin'],
            route: '/apps/admin/roles',
            icon: 'admin_panel_settings'
          },
          {
            type: 'link',
            label: 'WeChat Sign-ins',
            role: ['admin', 'sourcing_manager'],
            route: '/apps/admin/wechat-signins',
            icon: 'qr_code_2'
          },
        ]
      }
    ];

    this.configService.updateConfig({
      sidenav: {
        state: 'collapsed'
      },
      // The footer is intentionally commented out in custom-layout.component.html.
      // Leaving footer.visible true keeps the `has-footer` class on, which makes
      // scroll-disabled pages reserve --footer-height (56px) of dead space at the
      // bottom for a footer that never renders. Turn it off so content fills.
      footer: {
        visible: false
      }
    });
  }
}
